// libraries
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Button } from '@mui/material_modern'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

// components
import Loading from 'components/generic/Loading'

// services
import { useGetInboundTicketsQuery } from 'services/inboundTickets'

// state
import { setMergeTicketsState } from 'state/bill'

// utils & helpers
import { TICKET_STATUSES } from 'utils/constants'

const CombineTicketsAlert = ({ customerId }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [showAlert, setShowAlert] = useState(false)
  const [billsState, setBillsState] = useState({})

  const { settings } = useSelector(state => state.settings)
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}

  const { enableLockAccountingV2 } = useSelector(state => state.featureFlags?.variables || {})

  const { data: inboundTicketsData, isLoading: isLoadingInboundTickets } = useGetInboundTicketsQuery(
    {
      filter: JSON.stringify({
        statuses: [
          TICKET_STATUSES.INCOMPLETE.id,
          TICKET_STATUSES.SCALE_COMPLETE.id,
          TICKET_STATUSES.PRICE_COMPLETE.id,
          TICKET_STATUSES.APPROVED.id,
          TICKET_STATUSES.TRUCK_IN_YARD.id,
        ],
        customerId,
      }),
    },
    { skip: !customerId, refetchOnMountOrArgChange: true },
  )

  const handleCombineBills = () => {
    dispatch(setMergeTicketsState({ ibtIds: billsState.ibtIds, shouldMergeTickets: true }))
    if (billsState.billId) navigate(`/app/purchases/bills/${billsState.billId}`)
    else navigate('/app/purchases/bills/new')
  }

  useEffect(() => {
    if (inboundTicketsData?.totalResultsCount > 1) {
      const ibtIds =
        inboundTicketsData.Items?.filter(ticket => {
          const inboundTicketIsLocked =
            !enableLockAccountingV2 && // if lock accounting V2 is turned on, we do not need to disable any fields
            lockAccountingPeriod &&
            lockAccountingPeriod.isLockedForAccounting &&
            new Date(ticket.effectiveDate).getTime() < new Date(lockAccountingPeriod.lockAccountingDate).getTime()
          const preventAddingTicketToBill =
            inboundTicketIsLocked &&
            ticket?.status?.value !== TICKET_STATUSES.PRICE_COMPLETE.id &&
            ticket?.status?.value !== TICKET_STATUSES.APPROVED.id
          const hasMaterials = ticket.materialTypeCommonNames?.length > 0
          return !preventAddingTicketToBill && hasMaterials
        }).map(ticket => ticket.id) || []
      if (ibtIds?.length > 1) {
        const billId = inboundTicketsData.Items?.find(ticket => ticket.bill?.isMultiTicket)?.bill?.id || ''
        setBillsState({ ibtIds, billId })
        setShowAlert(true)
      }
    }
  }, [inboundTicketsData, lockAccountingPeriod])

  if (isLoadingInboundTickets) return <Loading />

  return showAlert ? (
    <Alert
      severity="info"
      sx={{ mb: 2 }}
      action={
        <Button color="inherit" onClick={() => handleCombineBills()}>
          PAY TICKETS TOGETHER
        </Button>
      }
    >
      Customer has additional unpaid tickets
    </Alert>
  ) : null
}

CombineTicketsAlert.propTypes = {
  customerId: PropTypes.string,
}

CombineTicketsAlert.defaultProps = {
  customerId: '',
}

export default CombineTicketsAlert
